import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnnotation } from '@embedpdf/plugin-annotation/react'
import { PdfAnnotationSubtype } from '@embedpdf/models'
import type { AnnotationSelectionMenuProps } from '@embedpdf/plugin-annotation/react'
import { FloatingMenu, type FloatingMenuItem } from './FloatingMenu'

const MENU_GAP = 4

type ShowableStyle =
  | 'strokeColor'
  | 'fillColor'
  | 'strokeWidth'
  | 'opacity'
  | 'borderStyle'
  | 'fontFamily'
  | 'fontSize'
  | 'fontColor'

function getStyleControls(annotation: any): ShowableStyle[] {
  switch (annotation.type) {
    case PdfAnnotationSubtype.HIGHLIGHT:
    case PdfAnnotationSubtype.UNDERLINE:
    case PdfAnnotationSubtype.SQUIGGLY:
    case PdfAnnotationSubtype.STRIKEOUT:
      return ['strokeColor', 'opacity']
    case PdfAnnotationSubtype.INK:
      return ['strokeColor', 'strokeWidth', 'opacity']
    case PdfAnnotationSubtype.SQUARE:
    case PdfAnnotationSubtype.CIRCLE:
    case PdfAnnotationSubtype.POLYGON:
      return ['fillColor', 'strokeColor', 'strokeWidth', 'opacity', 'borderStyle']
    case PdfAnnotationSubtype.LINE:
    case PdfAnnotationSubtype.POLYLINE:
      return ['strokeColor', 'strokeWidth', 'opacity', 'borderStyle']
    case PdfAnnotationSubtype.FREETEXT:
      return ['fontFamily', 'fontSize', 'fontColor', 'fillColor', 'strokeColor', 'strokeWidth', 'opacity']
    case PdfAnnotationSubtype.REDACT:
      return ['fillColor', 'opacity']
    default:
      return ['opacity']
  }
}

const BORDER_STYLES: { label: string; value: string }[] = [
  { label: 'Solid', value: 'solid' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
  { label: 'Dash Dot', value: 'dashDot' },
  { label: 'Cloudy', value: 'cloudy' },
]

interface Props extends AnnotationSelectionMenuProps {
  documentId: string
}

export const AnnotationSelectionMenu = ({
  documentId,
  selected,
  menuWrapperProps,
  placement,
  context,
}: Props) => {
  const { t } = useTranslation()
  const { provides: annotationApi } = useAnnotation(documentId)
  const [showStyle, setShowStyle] = useState(false)

  const annotation = context?.annotation
  const ann = annotation?.object
  const pageIndex = context?.pageIndex
  const id = ann?.id
  const structurallyLocked = context?.structurallyLocked ?? false

  const controls = ann ? getStyleControls(ann) : []

  const [styleState, setStyleState] = useState<Record<string, any>>({})

  useEffect(() => {
    if (!selected) {
      setShowStyle(false)
    }
  }, [selected])

  useEffect(() => {
    if (!ann) return
    const initial: Record<string, any> = {}
    for (const ctrl of controls) {
      if (ctrl === 'strokeColor') initial[ctrl] = (ann as any).strokeColor || (ann as any).color || '#FF0000'
      else if (ctrl === 'fillColor') initial[ctrl] = (ann as any).color || '#000000'
      else if (ctrl === 'strokeWidth') initial[ctrl] = (ann as any).strokeWidth ?? 2
      else if (ctrl === 'opacity') initial[ctrl] = (ann as any).opacity ?? 0.8
      else if (ctrl === 'borderStyle') {
        const bs = (ann as any).strokeStyle
        if (typeof bs === 'number') {
          const map: Record<number, string> = { 0: 'solid', 1: 'solid', 2: 'dashed', 3: 'beveled', 4: 'inset', 5: 'underline', 6: 'cloudy' }
          initial[ctrl] = map[bs] || 'solid'
        } else {
          initial[ctrl] = bs ?? 'solid'
        }
      }
      else if (ctrl === 'fontFamily') initial[ctrl] = (ann as any).fontFamily ?? 'Arial'
      else if (ctrl === 'fontSize') initial[ctrl] = (ann as any).fontSize ?? 12
      else if (ctrl === 'fontColor') initial[ctrl] = (ann as any).fontColor ?? '#000000'
    }
    setStyleState(initial)
  }, [ann?.id])

  const handleStyleChange = useCallback((key: string, value: any) => {
    setStyleState((prev: Record<string, any>) => {
      const next = { ...prev, [key]: value }
      if (annotationApi && id && pageIndex != null) {
        const patch: Record<string, any> = {}
        if (key === 'strokeColor') patch.strokeColor = value
        else if (key === 'fillColor') patch.color = value
        else if (key === 'strokeWidth') patch.strokeWidth = value
        else if (key === 'opacity') patch.opacity = value
        else if (key === 'borderStyle') {
          const styleMap: Record<string, number> = { 'solid': 1, 'dashed': 2, 'beveled': 3, 'inset': 4, 'underline': 5, 'cloudy': 6, 'dotted': 3 }
          patch.strokeStyle = styleMap[value] ?? 1
          if (value === 'dashed' || value === 'dotted' || value === 'dashDot') {
            const sw = (ann as any).strokeWidth || prev.strokeWidth || 2
            if (value === 'dashed') patch.strokeDashArray = [sw * 3, sw * 2]
            else if (value === 'dotted') patch.strokeDashArray = [sw, sw * 2]
            else if (value === 'dashDot') patch.strokeDashArray = [sw * 4, sw * 2, sw, sw * 2]
          } else if (value === 'solid') {
            patch.strokeDashArray = undefined
          } else if (value === 'cloudy') {
            patch.cloudyBorderIntensity = 2
          }
        }
        else if (key === 'fontFamily') patch.fontFamily = value
        else if (key === 'fontSize') patch.fontSize = value
        else if (key === 'fontColor') patch.fontColor = value
        annotationApi.updateAnnotation(pageIndex, id, patch)
      }
      return next
    })
  }, [annotationApi, ann, id, pageIndex])

  const handleDelete = useCallback(() => {
    if (!annotationApi || !id || pageIndex == null) return
    annotationApi.deleteAnnotation(pageIndex, id)
  }, [annotationApi, id, pageIndex])

  const handleToggleStyle = useCallback(() => {
    setShowStyle((prev) => !prev)
  }, [])

  const handleDone = useCallback(() => {
    if (!annotationApi) return
    annotationApi.deselectAnnotation()
  }, [annotationApi])

  useEffect(() => {
    if (!selected) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        annotationApi?.deselectAnnotation()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [selected, annotationApi])

  if (!selected || !ann) return null

  const items: FloatingMenuItem[] = [
    {
      id: 'style',
      icon: 'palette',
      iconColor: '#60A5FA',
      tooltip: t('tooltips.styleSettings') ?? 'Style',
      onClick: handleToggleStyle,
      disabled: structurallyLocked,
    },
    {
      id: 'delete',
      icon: 'trash',
      iconColor: '#EF4444',
      tooltip: t('tooltips.delete') ?? 'Delete',
      onClick: handleDelete,
      disabled: structurallyLocked,
    },
    {
      id: 'done',
      icon: 'check',
      iconColor: '#22C55E',
      tooltip: t('tooltips.done') ?? 'Done',
      onClick: handleDone,
    },
  ]

  const { style: wrapperStyle, ref } = menuWrapperProps
  const menuHeight = showStyle ? 240 : 44
  const menuTop = placement.suggestTop ? -(menuHeight + MENU_GAP) : 48

  return (
    <div ref={ref} style={wrapperStyle}>
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          top: menuTop,
          zIndex: 1000,
          pointerEvents: 'auto',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerDownCapture={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center gap-1">
          <FloatingMenu items={items} />
          {showStyle && controls.length > 0 && (
            <div
              className="bg-[#1e293b] rounded-xl p-3 shadow-2xl border border-white/10 flex flex-col gap-2 min-w-[180px]"
              style={{ pointerEvents: 'auto' }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseDownCapture={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerDownCapture={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
            >
              {controls.map((ctrl) => (
                <StyleControl
                  key={ctrl}
                  control={ctrl}
                  value={styleState[ctrl]}
                  onChange={(v: any) => handleStyleChange(ctrl, v)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const StyleControl = ({ control, value, onChange }: { control: string; value: any; onChange: (v: any) => void }) => {
  const { t } = useTranslation()

  switch (control) {
    case 'strokeColor':
    case 'fillColor':
    case 'fontColor':
      return (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-white/70 whitespace-nowrap">
            {control === 'strokeColor' ? (t('style.strokeColor') ?? 'Stroke') : control === 'fillColor' ? (t('style.fillColor') ?? 'Fill') : (t('style.fontColor') ?? 'Font')}
          </span>
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent p-0"
          />
        </div>
      )
    case 'strokeWidth':
      return (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-white/70 whitespace-nowrap">{t('style.strokeWidth') ?? 'Width'}</span>
          <div className="flex items-center gap-1">
            <input
              type="range"
              min={1}
              max={20}
              step={1}
              value={value ?? 2}
              onChange={(e) => onChange(Number(e.target.value))}
              className="w-16 h-1 accent-blue-400"
            />
            <span className="text-xs text-white/50 w-5 text-right">{value ?? 2}</span>
          </div>
        </div>
      )
    case 'opacity':
      return (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-white/70 whitespace-nowrap">{t('style.opacity') ?? 'Opacity'}</span>
          <div className="flex items-center gap-1">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round((value ?? 0.8) * 100)}
              onChange={(e) => onChange(Number(e.target.value) / 100)}
              className="w-16 h-1 accent-blue-400"
            />
            <span className="text-xs text-white/50 w-6 text-right">{Math.round((value ?? 0.8) * 100)}%</span>
          </div>
        </div>
      )
    case 'borderStyle':
      return (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-white/70 whitespace-nowrap">{t('style.borderStyle') ?? 'Border'}</span>
          <select
            value={value || 'solid'}
            onChange={(e) => onChange(e.target.value)}
            className="bg-white/10 text-white text-xs rounded px-2 py-0.5 border border-white/20 outline-none"
          >
            {BORDER_STYLES.map((bs) => (
              <option key={bs.value} value={bs.value} className="bg-[#1e293b] text-white">
                {bs.label}
              </option>
            ))}
          </select>
        </div>
      )
    case 'fontFamily':
      return (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-white/70 whitespace-nowrap">{t('style.fontFamily') ?? 'Font'}</span>
          <select
            value={value || 'Arial'}
            onChange={(e) => onChange(e.target.value)}
            className="bg-white/10 text-white text-xs rounded px-2 py-0.5 border border-white/20 outline-none"
          >
            <option value="Arial" className="bg-[#1e293b]">Arial</option>
            <option value="Courier New" className="bg-[#1e293b]">Courier</option>
            <option value="Times New Roman" className="bg-[#1e293b]">Times</option>
            <option value="Helvetica" className="bg-[#1e293b]">Helvetica</option>
          </select>
        </div>
      )
    case 'fontSize':
      return (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-white/70 whitespace-nowrap">{t('style.fontSize') ?? 'Size'}</span>
          <div className="flex items-center gap-1">
            <input
              type="range"
              min={6}
              max={72}
              step={1}
              value={value ?? 12}
              onChange={(e) => onChange(Number(e.target.value))}
              className="w-16 h-1 accent-blue-400"
            />
            <span className="text-xs text-white/50 w-5 text-right">{value ?? 12}</span>
          </div>
        </div>
      )
    default:
      return null
  }
}
